import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Coins, FileSpreadsheet, FileText, Info } from "lucide-react";
import { useAuth } from "@/context/use-auth";
import { useMembersTranslation } from "@/i18n/hooks";
import { Badge } from "@/components/tailwind/badge";
import { Heading } from "@/components/tailwind/heading";
import { Text } from "@/components/tailwind/text";
import { Button } from "@/components/tailwind/button";
import { Card } from "@/components/ui/card";
import { Input, InputGroup } from "@/components/tailwind/input";
import { Checkbox } from "@/components/tailwind/checkbox";
import { Field, Label } from "@/components/tailwind/fieldset";
import { MagnifyingGlassIcon } from "@heroicons/react/16/solid";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/tailwind/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { formatDateString, formatDateTimeString } from "@/lib/utils";
import {
    downloadMemberPointsStatementExcel,
    downloadMemberPointsStatementPdf,
    getMemberPointsStatement,
    MemberPointsStatement,
    MemberPointsStatementEntry,
    MemberPointsStatementMember,
    StatementDateRange
} from "@/services/member-points-statement-services";
import { DateRange, PeriodStepperFilter } from "@/components/date-range/period-stepper-filter";
import log from '@/lib/logger'

const SEARCH_DEBOUNCE_MS = 300;

const FORMER_MEMBERS_ROUTE = '/reports/former-members';
// Whitelisted origins for the back link; anything else falls back to the resigned heuristic.
const VALID_FROM_PARAMS = ['member', 'former-members'] as const;

function backTarget(
    from: string | null,
    memberId: string | undefined,
    resigned: boolean | undefined,
): { to: string; labelKey: string } {
    const memberRoute = { to: `/members/${memberId}`, labelKey: 'memberPointsStatement.backToMember' };
    const listRoute = { to: FORMER_MEMBERS_ROUTE, labelKey: 'memberPointsStatement.backToList' };
    // A resigned member has no detail page, so never point back at it.
    if (resigned) return listRoute;
    if (from && (VALID_FROM_PARAMS as readonly string[]).includes(from)) {
        return from === 'member' ? memberRoute : listRoute;
    }
    return memberRoute;
}

function triggerDownload(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
}

function MemberHeader({ member }: { member: MemberPointsStatementMember }) {
    const { t } = useMembersTranslation();
    const memberLabel = `#${member.memberNo} ${member.firstName} ${member.lastName}`;

    return (
        <Card className="p-6 space-y-2" data-testid="points-statement-member-header">
            <div className="flex items-center gap-3">
                {member.resigned ? (
                    <Link
                        to={`/members/${member.id}/retained-data`}
                        data-testid="points-statement-retained-data-link"
                        className="text-lg font-semibold text-blue-600 hover:underline dark:text-blue-400"
                    >
                        {memberLabel}
                    </Link>
                ) : (
                    <Link
                        to={`/members/${member.id}`}
                        className="text-lg font-semibold text-blue-600 hover:underline dark:text-blue-400"
                    >
                        {memberLabel}
                    </Link>
                )}
                {member.resigned && (
                    <Badge color="amber" data-testid="points-statement-resigned-badge">
                        {t('memberPointsStatement.header.resignedBadge')}
                    </Badge>
                )}
            </div>
            <Text className="text-sm text-zinc-500 dark:text-zinc-400">
                {member.membershipEnd
                    ? t('memberPointsStatement.header.membership', {
                        start: formatDateString(member.membershipStart),
                        end: formatDateString(member.membershipEnd)
                    })
                    : t('memberPointsStatement.header.memberSince', {
                        date: formatDateString(member.membershipStart)
                    })}
            </Text>
        </Card>
    );
}

function ReferenceCell({ entry }: { entry: MemberPointsStatementEntry }) {
    const { t } = useMembersTranslation();

    if (entry.orderId) {
        return (
            <Link
                to={`/orders/${entry.orderId}`}
                className="text-blue-600 hover:underline dark:text-blue-400"
            >
                {t('memberPointsStatement.reference.order', { number: entry.orderNumber ?? entry.orderId })}
            </Link>
        );
    }
    if (entry.specialAssignmentBookingId) {
        return (
            <Link
                to="/members/special-assignments"
                className="text-blue-600 hover:underline dark:text-blue-400"
            >
                {t('memberPointsStatement.reference.specialAssignment')}
            </Link>
        );
    }
    return <span className="text-zinc-400">–</span>;
}

function PointsCell({ entry }: { entry: MemberPointsStatementEntry }) {
    const isCredit = entry.direction === "CREDIT";
    const sign = isCredit ? "+" : "−";
    const color = isCredit ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400";

    return (
        <span className={`font-medium tabular-nums ${color}`}>
            {sign}{entry.amount}
        </span>
    );
}

function BookingInfo({ entry }: { entry: MemberPointsStatementEntry }) {
    const { t } = useMembersTranslation();

    return (
        <div className="space-y-2 text-xs">
            <div className="flex justify-between items-start gap-2">
                <span className="text-zinc-500 dark:text-zinc-400 shrink-0">
                    {t('memberPointsStatement.info.bookedAt')}:
                </span>
                <span className="text-right text-zinc-900 dark:text-zinc-100">
                    {formatDateTimeString(entry.bookedAt)}
                </span>
            </div>
            <div className="flex justify-between items-start gap-2">
                <span className="text-zinc-500 dark:text-zinc-400 shrink-0">
                    {t('memberPointsStatement.info.bookedBy')}:
                </span>
                <span className="text-right text-zinc-900 dark:text-zinc-100">{entry.bookedByName ?? '–'}</span>
            </div>
        </div>
    );
}

export default function MemberPointsStatementPage() {
    const { memberId } = useParams();
    const [searchParams] = useSearchParams();
    const { token } = useAuth();
    const { t } = useMembersTranslation();
    const { toast } = useToast();
    const [statement, setStatement] = useState<MemberPointsStatement | null>(null);
    const [range, setRange] = useState<StatementDateRange | null>(null);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [searchAll, setSearchAll] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    const handleRangeChange = useCallback((period: DateRange | null) => {
        setRange(period ? { from: period.startDate, to: period.endDate } : null);
    }, []);

    // Debounce so typing does not fire one backend request per keystroke.
    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [search]);

    // Clearing the search must not leave a stale "all periods" choice armed.
    useEffect(() => {
        if (search.trim() === '') setSearchAll(false);
    }, [search]);

    const activeSearch = debouncedSearch.trim();
    const isSearching = activeSearch.length > 0;
    // searchAll drops the range so the search spans the whole account.
    const effectiveRange = isSearching && searchAll ? null : range;

    const canExport = !isLoading && !hasError && (statement?.entries.length ?? 0) > 0;

    async function handleExport(
        download: (token: string) => Promise<{ blob: Blob; filename: string }>
    ) {
        if (!token) return;
        try {
            const { blob, filename } = await download(token);
            triggerDownload(blob, filename);
        } catch (error) {
            log.error("Error downloading member points statement export:", error);
            toast({ title: t('memberPointsStatement.export.error'), variant: 'destructive' });
        }
    }

    useEffect(() => {
        if (!token || !memberId) return;
        let ignore = false;
        setIsLoading(true);
        setHasError(false);
        getMemberPointsStatement(token, memberId, effectiveRange, activeSearch || undefined)
            .then((data) => { if (!ignore) setStatement(data); })
            .catch((error) => {
                if (ignore) return;
                log.error("Error loading member points statement:", error);
                setHasError(true);
            })
            .finally(() => { if (!ignore) setIsLoading(false); });
        // Ignore stale responses so a slow earlier query cannot overwrite a newer one.
        return () => { ignore = true; };
    }, [token, memberId, effectiveRange, activeSearch]);

    const back = backTarget(searchParams.get('from'), memberId, statement?.member.resigned);

    return (
        <div className="container mx-auto py-6 max-w-7xl space-y-6">
            <Link
                to={back.to}
                data-testid="points-statement-back-link"
                className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 hover:underline dark:text-zinc-400 dark:hover:text-zinc-100"
            >
                <ArrowLeft className="h-4 w-4"/>
                {t(back.labelKey)}
            </Link>
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                    <Coins className="h-8 w-8 text-blue-500" />
                    <div>
                        <Heading>{t('memberPointsStatement.title')}</Heading>
                        <Text className="text-sm text-zinc-500 dark:text-zinc-400">
                            {t('memberPointsStatement.subtitle')}
                        </Text>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        plain
                        onClick={() => handleExport((tok) => downloadMemberPointsStatementExcel(
                            tok, memberId ?? '', effectiveRange, activeSearch || undefined))}
                        disabled={!canExport}
                        className="flex items-center gap-2"
                        title={t('memberPointsStatement.export.excel')}
                    >
                        <FileSpreadsheet className="h-4 w-4" />
                        <span className="hidden sm:inline">{t('memberPointsStatement.export.excel')}</span>
                    </Button>
                    <Button
                        plain
                        onClick={() => handleExport((tok) => downloadMemberPointsStatementPdf(
                            tok, memberId ?? '', effectiveRange, activeSearch || undefined))}
                        disabled={!canExport}
                        className="flex items-center gap-2"
                        title={t('memberPointsStatement.export.pdf')}
                    >
                        <FileText className="h-4 w-4" />
                        <span className="hidden sm:inline">{t('memberPointsStatement.export.pdf')}</span>
                    </Button>
                </div>
            </div>

            {statement && <MemberHeader member={statement.member} />}

            <Card className="p-6 space-y-4">
                <div className="flex flex-wrap items-end gap-4">
                    <PeriodStepperFilter
                        onChange={handleRangeChange}
                        translationNamespace="members"
                        translationKeyPrefix="memberPointsStatement.timePeriod"
                        includeFullAccount
                        defaultType="fullAccount"
                    />
                    <Field className="flex-1 min-w-[220px]">
                        <Label>{t('memberPointsStatement.search.label')}</Label>
                        <InputGroup>
                            <MagnifyingGlassIcon />
                            <Input
                                type="text"
                                placeholder={t('memberPointsStatement.search.placeholder')}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </InputGroup>
                    </Field>
                </div>
                {isSearching && range && (
                    <Field className="flex items-center gap-2">
                        <Checkbox checked={searchAll} onChange={setSearchAll} />
                        <Label>{t('memberPointsStatement.search.allPeriods')}</Label>
                    </Field>
                )}
            </Card>

            {isLoading ? (
                <Card className="p-6">
                    <Text className="text-zinc-500">{t('memberPointsStatement.loading')}</Text>
                </Card>
            ) : hasError || !statement ? (
                <Card className="p-6">
                    <Text className="text-red-500">{t('memberPointsStatement.error')}</Text>
                </Card>
            ) : (
                <>
                    <Card className="p-6">
                        {!isSearching && statement.from && (
                            <div className="mb-3">
                                <Text className="text-sm text-zinc-500 dark:text-zinc-400">
                                    {t('memberPointsStatement.balance.opening')}
                                </Text>
                                <div className="mt-1 text-lg font-semibold tabular-nums">
                                    {statement.openingBalance} {t('memberPointsStatement.balance.unit')}
                                </div>
                            </div>
                        )}
                        <Text className="text-sm text-zinc-500 dark:text-zinc-400">
                            {isSearching
                                ? t('memberPointsStatement.balance.matchedSum')
                                : statement.from
                                    ? t('memberPointsStatement.balance.closing')
                                    : t('memberPointsStatement.balance.label')}
                        </Text>
                        <div className="mt-1 text-3xl font-bold tabular-nums" data-testid="member-points-balance">
                            {statement.closingBalance} {t('memberPointsStatement.balance.unit')}
                        </div>
                    </Card>

                    <Card className="p-6">
                        {statement.entries.length === 0 ? (
                            <Text className="text-zinc-500 dark:text-zinc-400">
                                {t('memberPointsStatement.empty')}
                            </Text>
                        ) : (
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableHeader>{t('memberPointsStatement.columns.date')}</TableHeader>
                                        <TableHeader>{t('memberPointsStatement.columns.type')}</TableHeader>
                                        <TableHeader>{t('memberPointsStatement.columns.points')}</TableHeader>
                                        <TableHeader>{t('memberPointsStatement.columns.reference')}</TableHeader>
                                        <TableHeader>{t('memberPointsStatement.columns.description')}</TableHeader>
                                        <TableHeader className="text-right">
                                            {t('memberPointsStatement.columns.info')}
                                        </TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {statement.entries.map((entry) => (
                                        <TableRow key={entry.id} data-testid="points-statement-row">
                                            <TableCell className="whitespace-nowrap text-sm">
                                                {formatDateTimeString(entry.transactionTimestamp)}
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {t(`memberPointsStatement.sourceType.${entry.sourceType}`)}
                                            </TableCell>
                                            <TableCell>
                                                <PointsCell entry={entry} />
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                <ReferenceCell entry={entry} />
                                            </TableCell>
                                            <TableCell className="text-sm max-w-md truncate" title={entry.description}>
                                                {entry.description}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <button
                                                                    aria-label={t('memberPointsStatement.info.label')}
                                                                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                                                                >
                                                                    <Info className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                                                                </button>
                                                            </TooltipTrigger>
                                                            <TooltipContent
                                                                side="left"
                                                                className="w-64 p-3 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 opacity-100"
                                                            >
                                                                <BookingInfo entry={entry} />
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Card>
                </>
            )}
        </div>
    );
}
